/**
 * Put text on the clipboard, including where the async Clipboard API is not
 * there to be called.
 *
 * `navigator.clipboard` exists ONLY in a secure context — https, or localhost.
 * Served over plain http on a LAN address or an internal hostname, the property
 * is undefined, so `navigator.clipboard.writeText(...)` throws a TypeError
 * before it ever reaches a permission prompt. A Copy button whose only path was
 * that call, wrapped in a silent catch, did nothing at all and said nothing
 * about it.
 *
 * Returns whether the text actually landed, so the caller can tell the user
 * when it did not rather than showing "Copied" over an empty clipboard.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Blocked by permissions, or a non-secure context that defines the object
    // but refuses the write. Either way there is still the selection path.
  }
  return selectionCopy(text);
}

/**
 * The pre-Clipboard-API path: select text in a throwaway field and let the
 * document copy the selection. Deprecated, and the only thing that works
 * outside a secure context.
 */
function selectionCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // Off-screen, NOT hidden: `display:none` or `visibility:hidden` makes the
  // selection empty and the copy a silent no-op. Fixed positioning also keeps
  // the page from scrolling to it.
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  const previous = document.activeElement as HTMLElement | null;
  try {
    ta.select();
    ta.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
    previous?.focus?.();
  }
}
